import { useEffect, useState } from 'react';
import { __ } from '../functions/commom';
import SelectorBox from './base/SelectorBox';
import DateSelector from './base/DateSelector';
import moment from 'moment-timezone';
import InputBox from './base/InputBox';
import { useParams } from 'react-router-dom';
import { useAppState } from '../state/StateContext';
import {
  DATE_FORMAT,
  MAX_BUILDING_AGE_INPUT_LIMIT,
  MAX_FLOOR_AREA_INPUT_LIMIT,
  MIN_ADVANCE_HOURS,
  TIMEZONE,
} from '../config';
import { createUseStyles } from 'react-jss';
import { ReactComponent as CouponIcon } from '../assets/icons/coupon.svg';
import BlockSelector from './base/BlockSelector';

const useStyles = createUseStyles({
  SearchFormWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  planList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    '& .plan-item': {
      display: 'flex',
      gap: '5px',
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      cursor: 'pointer',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      transition: 'all 0.2s ease-in-out',
      '& .info': {
        padding: '15px',
        flex: 2,
        '& .title': {
          fontSize: '18px',
          fontWeight: 'bold',
          marginBottom: '10px',
          borderLeft: '5px solid var(--dai-primary-color)',
          paddingLeft: '10px',
        },
      },
      '& .apply_button': {
        backgroundColor: 'var(--dai-primary-color)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '10px',
        '& svg': {
          fill: '#ffffff',
          width: '20px',
          height: '20px',
        },
      },
      '& .price': {
        fontSize: '24px',
        fontWeight: 'bold',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--dai-primary-color)',
        gap: '5px',
        '& .symbol': {
          fontSize: '14px',
          color: '#666666',
        },
      },
      '&:hover': {
        color: '#fff',
        backgroundColor: 'var(--dai-primary-color)',
        '& .title': {
          borderColor: '#ffffff',
        },
        '& .price': {
          color: '#ffffff',
          '& .symbol': {
            color: '#ffffff',
          },
        },
      },
    },
  },
  planType: {
    fontSize: '14px',
  },
  planHighLight: {
    fontSize: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  planHighLightItem: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  dateRange: {
    display: 'flex',
    gap: '20px',
    '&> div': {
      flex: 1,
    },
  },
});

const SearchFormHome: React.FC = () => {
  const { category = 'home', id = 'goal_home_comprehensive_insurance' } =
    useParams<{
      category?: string;
      id?: string;
    }>();

  const classes = useStyles();
  const state = useAppState();

  const [couponCodeState, setCouponCodeState] = useState('');

  const insurancePlans = state?.insurancePlans?.get();

  // Minimum date is 48 hours from now (MIN_ADVANCE_HOURS) using HKT
  const minBookingDate = moment().tz(TIMEZONE).add(MIN_ADVANCE_HOURS, 'hours').format(DATE_FORMAT);
  const nextYear = moment(minBookingDate).add(364, 'day').format(DATE_FORMAT);

  const departureDate = state?.searchForm?.departureDate?.get();
  const departureDatePlusOneYear = departureDate
    ? moment(departureDate).add(364, 'day').format(DATE_FORMAT)
    : '';

  function handleIdChange(value: string | undefined) {
    // Locate to the new plan
    state?.searchForm?.id?.set(value || '');
    // redirect to the new plan
    // window.location.href = `/insurance/${category}/${value}`;
  }

  function handleSearchDepartureDateChange(date: string | null) {
    if (!date) return;
    const returnDate = state.searchForm.returnDate.get();

    // If return date is before departure date, reset return date
    if (returnDate && moment(returnDate).isBefore(date)) {
      state.searchForm.returnDate.set(null);
    }

    state.searchForm.departureDate.set(date);
  }

  function handleBuildingAgeChange(value: string | null | undefined) {
    if (value && parseInt(value) > MAX_BUILDING_AGE_INPUT_LIMIT) return;
    (value || value === '') &&
      state?.searchForm?.buildingAge?.set(parseInt(value));
  }

  function handleFloorAreaChange(value: string | null | undefined) {
    if (value && parseInt(value) > MAX_FLOOR_AREA_INPUT_LIMIT) return;
    (value || value === '') &&
      state?.searchForm?.floorArea?.set(parseInt(value));
  }

  const handleCouponCodeStateChange = (value: string | undefined) => {
    value && setCouponCodeState(value);
    state?.searchForm?.couponCode?.set(value || '');
  };

  // Ensure departure date is at least minBookingDate (48 hours in advance)
  useEffect(() => {
    const currentDepartureDate = state?.searchForm?.departureDate?.get();
    
    // If no departure date set or if it's before minBookingDate, set to minBookingDate
    if (!currentDepartureDate || moment(currentDepartureDate).isBefore(moment(minBookingDate))) {
      state?.searchForm?.departureDate?.set(minBookingDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minBookingDate]);

  // Debounce the coupon code
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      state.searchForm.couponCode.set(couponCodeState);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [couponCodeState, state.searchForm.couponCode]);

  return (
    <>
      <div className={classes.SearchFormWrapper}>
        <SelectorBox
          label={__('Plan')}
          options={
            Array.isArray(insurancePlans)
              ? insurancePlans
                  .filter((plan) => plan.category === category)
                  .map((plan) => ({
                    label: plan?.name || '',
                    value: plan?.id || '',
                  }))
              : []
          }
          value={id || state?.searchForm?.id?.get() || ''}
          onChange={(value) => handleIdChange(value)}
        />

        <InputBox
          required
          type="number"
          max={MAX_BUILDING_AGE_INPUT_LIMIT}
          min={0}
          label={__('Building Age')}
          placeholder={__(
            `Building age limit: $1 years`,
            MAX_BUILDING_AGE_INPUT_LIMIT
          )}
          value={state?.searchForm?.buildingAge?.get()?.toString() || undefined}
          onChange={handleBuildingAgeChange}
        />

        <BlockSelector
          label={__('Area Calculation')}
          options={[
            { label: __('Net'), value: 'net' },
            { label: __('Gross'), value: 'gross' },
          ]}
          value={state?.searchForm?.areaType?.get() || 'net'}
          callback={(value) => state?.searchForm?.areaType?.set(value)}
        />

        <InputBox
          required
          type="number"
          max={MAX_FLOOR_AREA_INPUT_LIMIT}
          min={0}
          label={__('Total Area (sqft)')}
          placeholder={__(`Area limit: $1 sqft`, MAX_FLOOR_AREA_INPUT_LIMIT)}
          value={state?.searchForm?.floorArea?.get()?.toString() || undefined}
          onChange={handleFloorAreaChange}
        />

        <div className={classes.dateRange}>
          <DateSelector
            label={__('Effective Date')}
            value={
              state?.searchForm?.departureDate?.get()
                ? moment(state?.searchForm?.departureDate?.get()).format(
                    DATE_FORMAT
                  )
                : null
            }
            onChange={handleSearchDepartureDateChange}
            minDate={minBookingDate}
            maxDate={nextYear}
            required
          />
          <InputBox
            label={__('End Date')}
            value={departureDatePlusOneYear}
            placeholder={__('Please select Effective Date first')}
            disabled={true}
          />
        </div>

        <hr />

        <InputBox
          placeholder={__('Coupon Code') + ' ' + __('(If applicable)')}
          value={couponCodeState}
          onChange={handleCouponCodeStateChange}
          icon={<CouponIcon />}
          iconPosition="before"
        />
      </div>
    </>
  );
};

export default SearchFormHome;
